import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Graphiques SVG légers (components/Chart, `VX.plot` / `VX.hbars` de vx.js),
 * sans librairie : courbes et barres dans le temps, barres horizontales de
 * comparaison, jauge de seuil, barre de répartition, légende.
 *
 * Règles (design system) :
 * - couleur par série via `ChartColor` (`chart-1` maintenance / abonnements,
 *   `chart-2` usage IA refacturé, `chart-3` marge ou encaissé, `chart-4`
 *   catégorie en plus, `chart-5` autres) ; la même série garde la même couleur ;
 * - chaque série se distingue aussi par une forme (marqueur rond ou carré,
 *   trait plein ou pointillé) et figure dans une `ChartLegend` ;
 * - `chart-4` et `chart-5` sont proches pour un daltonien : ne jamais les
 *   juxtaposer sans légende ni forme distincte ;
 * - grille en `border`, libellés d'axe 11px `muted-foreground`, aucun dégradé ;
 * - `aria-label` obligatoire : une phrase qui résume la tendance (role="img").
 */

export type ChartColor = 'chart-1' | 'chart-2' | 'chart-3' | 'chart-4' | 'chart-5';

const colorVar = (color: ChartColor): string => `var(--${color})`;

/* -------------------------------------------------------------------------- */
/* Légende                                                                     */
/* -------------------------------------------------------------------------- */

export interface LegendItem {
  label: React.ReactNode;
  color: ChartColor;
  /** Forme du repère : carré (barres, défaut), disque (courbe à marqueurs ronds), trait. */
  shape?: 'square' | 'dot' | 'line';
  /** Valeur affichée après le libellé (mono, `foreground`). */
  value?: React.ReactNode;
}

/** `chart-legend` : repères 10px + libellés 12px `muted-foreground`. */
function ChartLegend({
  items,
  className,
}: {
  items: readonly LegendItem[];
  className?: string;
}): React.JSX.Element {
  return (
    <div
      className={cn('flex flex-wrap gap-x-4 gap-y-2 text-caption text-muted-foreground', className)}
    >
      {items.map((item, index) => (
        <span key={index} className="inline-flex items-center gap-1.5">
          <i
            aria-hidden="true"
            className={cn(
              'inline-block w-2.5',
              item.shape === 'line' ? 'h-0.5' : 'h-2.5',
              item.shape === 'dot' && 'rounded-full',
            )}
            style={{ background: colorVar(item.color) }}
          />
          {item.label}
          {item.value !== undefined ? (
            <b className="font-mono font-medium text-foreground">{item.value}</b>
          ) : null}
        </span>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Géométrie (port de VX.plot)                                                 */
/* -------------------------------------------------------------------------- */

export interface BarSeries {
  key: string;
  label: string;
  color: ChartColor;
  /** Une valeur par catégorie (0 si absente). */
  values: readonly number[];
}

export interface LineSeries {
  key: string;
  label: string;
  color: ChartColor;
  /** Une valeur par catégorie ; `null` interrompt la courbe. */
  values: readonly (number | null)[];
  /** Trait pointillé. */
  dashed?: boolean;
  /** Marqueur : disque (défaut), carré, aucun. */
  marker?: 'circle' | 'square' | 'none';
  /** Marquer seulement certaines catégories (ex. jours où la valeur change). */
  markAt?: readonly boolean[];
}

function niceStep(x: number): number {
  if (x <= 0 || !Number.isFinite(x)) {
    return 1;
  }
  const power = Math.pow(10, Math.floor(Math.log10(x)));
  const mantissa = x / power;
  const candidates = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
  for (const candidate of candidates) {
    if (mantissa <= candidate + 1e-9) {
      return candidate * power;
    }
  }
  return 10 * power;
}

interface TimeChartProps {
  /** Libellés de l'axe horizontal (jours, mois). */
  categories: readonly string[];
  bars?: readonly BarSeries[];
  lines?: readonly LineSeries[];
  /** Barres empilées (seulement si le total compte). */
  stacked?: boolean;
  /** Résumé de la tendance, lu par les lecteurs d'écran. */
  ariaLabel: string;
  /** Format des graduations et de l'infobulle (même format que les tableaux). */
  formatValue?: (value: number) => string;
  /** Nombre d'intervalles de graduation (3 par défaut). */
  ticks?: number;
  /** Afficher un libellé de catégorie sur `every`. */
  every?: number;
  /** Minimum de l'axe (0 par défaut ; un taux de change n'en part pas). */
  yMin?: number;
  yMax?: number;
  /** Dimensions du viewBox (le SVG occupe toute la largeur). */
  width?: number;
  height?: number;
  /** Marge gauche pour les graduations. */
  padLeft?: number;
  /** Largeur maximale d'une barre. */
  maxBar?: number;
  className?: string;
}

const defaultFormat = (value: number): string =>
  value.toLocaleString('fr-FR', { maximumFractionDigits: 0 });

/**
 * Courbes et/ou barres dans le temps (`VX.plot`) : « Requêtes par jour »,
 * « Usage IA cumulé », « Taux USD vers EUR ». Infobulle native par catégorie
 * (survol d'une colonne entière), colonne survolée surlignée.
 */
function TimeChart({
  categories,
  bars = [],
  lines = [],
  stacked = false,
  ariaLabel,
  formatValue = defaultFormat,
  ticks = 3,
  every = 1,
  yMin = 0,
  yMax,
  width = 760,
  height = 220,
  padLeft = 44,
  maxBar,
  className,
}: TimeChartProps): React.JSX.Element {
  const padRight = 12;
  const padTop = 12;
  const padBottom = 26;
  const innerWidth = width - padLeft - padRight;
  const innerHeight = height - padTop - padBottom;
  const count = Math.max(categories.length, 1);
  const step = innerWidth / count;

  let max = 0;
  categories.forEach((_, index) => {
    let sum = 0;
    for (const series of bars) {
      const value = series.values[index] ?? 0;
      if (stacked) {
        sum += value;
      } else if (value > max) {
        max = value;
      }
    }
    if (stacked && sum > max) {
      max = sum;
    }
    for (const series of lines) {
      const value = series.values[index];
      if (value !== null && value !== undefined && value > max) {
        max = value;
      }
    }
  });
  const top = yMax ?? yMin + niceStep((max - yMin) / ticks) * ticks;
  const span = top - yMin === 0 ? 1 : top - yMin;
  const y = (value: number): number => padTop + innerHeight * (1 - (value - yMin) / span);

  const grid = Array.from({ length: ticks + 1 }, (_, tick) => {
    const value = yMin + (span * tick) / ticks;
    return { value, y: y(value) };
  });

  const barWidth = stacked
    ? Math.min(maxBar ?? 28, step * 0.62)
    : Math.min(maxBar ?? 22, (step * 0.7) / Math.max(bars.length, 1));

  const [hovered, setHovered] = React.useState<number | null>(null);

  const tooltip = (index: number): string => {
    const parts = [
      ...bars.map((series) => `${series.label} : ${formatValue(series.values[index] ?? 0)}`),
      ...lines.map((series) => {
        const value = series.values[index];
        return `${series.label} : ${value === null || value === undefined ? '-' : formatValue(value)}`;
      }),
    ];
    return `${categories[index] ?? ''}\n${parts.join('\n')}`;
  };

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={ariaLabel}
      className={cn('block h-auto w-full', className)}
      onMouseLeave={() => {
        setHovered(null);
      }}
    >
      {grid.map((line) => (
        <g key={line.value}>
          <line
            x1={padLeft}
            x2={width - padRight}
            y1={line.y}
            y2={line.y}
            stroke="var(--border)"
            strokeWidth={1}
          />
          <text
            x={padLeft - 8}
            y={line.y + 4}
            textAnchor="end"
            fill="var(--muted-foreground)"
            style={{ font: '400 11px var(--font-sans)' }}
          >
            {formatValue(line.value)}
          </text>
        </g>
      ))}

      {hovered !== null ? (
        <rect
          x={padLeft + step * hovered}
          y={padTop}
          width={step}
          height={innerHeight}
          fill="var(--accent)"
          opacity={0.6}
          pointerEvents="none"
        />
      ) : null}

      {categories.map((_, index) => {
        const center = padLeft + step * (index + 0.5);
        let accumulated = 0;
        return bars.map((series, seriesIndex) => {
          const value = series.values[index] ?? 0;
          if (value === 0) {
            return null;
          }
          const x = stacked
            ? center - barWidth / 2
            : center - (barWidth * bars.length) / 2 + barWidth * seriesIndex;
          const topY = stacked ? y(accumulated + value) : y(value);
          const baseY = stacked ? y(accumulated) : y(yMin);
          if (stacked) {
            accumulated += value;
          }
          return (
            <rect
              key={`${series.key}-${index}`}
              x={x}
              y={topY}
              width={Math.max(barWidth - (stacked ? 0 : 1), 1)}
              // 1px d'écart entre segments empilés (filet de surface).
              height={Math.max(baseY - topY - (stacked ? 1 : 0), 1)}
              rx={2}
              fill={colorVar(series.color)}
              pointerEvents="none"
            />
          );
        });
      })}

      {lines.map((series) => {
        let path = '';
        let started = false;
        const marks: React.JSX.Element[] = [];
        series.values.forEach((value, index) => {
          if (value === null) {
            started = false;
            return;
          }
          const px = padLeft + step * (index + 0.5);
          const py = y(value);
          path += `${started ? 'L' : 'M'}${px.toFixed(1)} ${py.toFixed(1)} `;
          started = true;
          const marker = series.marker ?? 'circle';
          if (marker === 'none' || (series.markAt !== undefined && series.markAt[index] !== true)) {
            return;
          }
          marks.push(
            marker === 'square' ? (
              <rect
                key={index}
                x={px - 3.5}
                y={py - 3.5}
                width={7}
                height={7}
                fill={colorVar(series.color)}
                stroke="var(--card)"
                strokeWidth={2}
                pointerEvents="none"
              />
            ) : (
              <circle
                key={index}
                cx={px}
                cy={py}
                r={3.5}
                fill={colorVar(series.color)}
                stroke="var(--card)"
                strokeWidth={2}
                pointerEvents="none"
              />
            ),
          );
        });
        return (
          <g key={series.key}>
            <path
              d={path}
              fill="none"
              stroke={colorVar(series.color)}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              strokeDasharray={series.dashed === true ? '5 4' : undefined}
              pointerEvents="none"
            />
            {marks}
          </g>
        );
      })}

      {categories.map((label, index) =>
        index % every === 0 ? (
          <text
            key={`x-${index}`}
            x={padLeft + step * (index + 0.5)}
            y={height - 6}
            textAnchor="middle"
            fill="var(--muted-foreground)"
            style={{ font: '400 11px var(--font-sans)' }}
          >
            {label}
          </text>
        ) : null,
      )}

      {/* Zones de survol : une colonne par catégorie, plus large que la marque. */}
      {categories.map((_, index) => (
        <rect
          key={`hit-${index}`}
          x={padLeft + step * index}
          y={padTop}
          width={step}
          height={innerHeight}
          fill="transparent"
          onMouseEnter={() => {
            setHovered(index);
          }}
        >
          <title>{tooltip(index)}</title>
        </rect>
      ))}
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* Barres horizontales (hbars)                                                 */
/* -------------------------------------------------------------------------- */

export interface HBarItem {
  key: string;
  name: React.ReactNode;
  value: number;
  /** Valeur formatée à droite (« 208,10 € »). */
  label: string;
  /** Couleur propre à cette barre (sinon celle de la liste). */
  color?: ChartColor;
}

interface HBarsProps {
  items: readonly HBarItem[];
  ariaLabel: string;
  color?: ChartColor;
  /** Maximum de l'échelle (le plus grand élément par défaut). */
  max?: number;
  className?: string;
}

/** `hbars` : comparer des clients ou des postes (nom, piste 8px, valeur mono). */
function HBars({
  items,
  ariaLabel,
  color = 'chart-3',
  max,
  className,
}: HBarsProps): React.JSX.Element {
  const scale = max ?? Math.max(...items.map((item) => item.value), 0);
  return (
    <div role="img" aria-label={ariaLabel} className={cn('grid gap-1', className)}>
      {items.map((item) => {
        const pct = scale > 0 ? Math.max((item.value / scale) * 100, item.value > 0 ? 1.5 : 0) : 0;
        return (
          <div
            key={item.key}
            className="grid min-h-7 grid-cols-[minmax(0,150px)_minmax(0,1fr)_auto] items-center gap-3 text-[13px] leading-[18px]"
            title={`${typeof item.name === 'string' ? item.name : ''} ${item.label}`.trim()}
          >
            <span className="truncate">{item.name}</span>
            <svg
              viewBox="0 0 100 8"
              preserveAspectRatio="none"
              width="100%"
              height="8"
              className="block"
              aria-hidden="true"
            >
              <rect x="0" y="0" width="100" height="8" rx="1" fill="var(--muted)" />
              <rect
                x="0"
                y="0"
                width={Math.min(pct, 100)}
                height="8"
                rx="1"
                fill={colorVar(item.color ?? color)}
              />
            </svg>
            <span className="whitespace-nowrap text-right font-mono tabular-nums">
              {item.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Jauge de seuil (meter)                                                      */
/* -------------------------------------------------------------------------- */

interface MeterProps {
  /** Pourcentage consommé (peut dépasser 100). */
  percent: number;
  /** Repère de seuil en pourcentage (80 = alerte à 80 %). `null` : aucun repère. */
  threshold?: number | null;
  /** Hauteur de la piste (6 par défaut). */
  height?: number;
  color?: ChartColor;
  /** Fourni : la jauge est une image accessible ; sinon décorative (la valeur est écrite à côté). */
  ariaLabel?: string;
  className?: string;
}

/** Jauge de consommation avec repère de seuil (Clients, Alertes). */
function Meter({
  percent,
  threshold = 80,
  height = 6,
  color = 'chart-2',
  ariaLabel,
  className,
}: MeterProps): React.JSX.Element {
  const width = Math.min(Math.max(percent, 0), 100);
  return (
    <svg
      viewBox={`0 0 100 ${height}`}
      preserveAspectRatio="none"
      width="100%"
      height={height}
      className={cn('block min-w-24', className)}
      {...(ariaLabel !== undefined
        ? { role: 'img', 'aria-label': ariaLabel }
        : { 'aria-hidden': true })}
    >
      <rect x="0" y="0" width="100" height={height} rx="1" fill="var(--muted)" />
      <rect x="0" y="0" width={width} height={height} rx="1" fill={colorVar(color)} />
      {threshold !== null ? (
        <line
          x1={threshold}
          x2={threshold}
          y1="0"
          y2={height}
          stroke="var(--input)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
    </svg>
  );
}

interface MeterCellProps extends MeterProps {
  /** Sous la jauge, à gauche (« 9,10 € »). */
  start: React.ReactNode;
  /** Sous la jauge, à droite (« 91 % »). */
  end: React.ReactNode;
}

/** `meter-cell` : jauge + valeurs mono 12px dessous (cellule de tableau, 180px min). */
function MeterCell({ start, end, className, ...meter }: MeterCellProps): React.JSX.Element {
  return (
    <div className={cn('grid min-w-[180px] gap-1.5', className)}>
      <Meter {...meter} />
      <div className="flex justify-between gap-2 font-mono text-xs text-muted-foreground">
        <span>{start}</span>
        <span>{end}</span>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Barre de répartition                                                        */
/* -------------------------------------------------------------------------- */

export interface ProportionSegment {
  key: string;
  value: number;
  color: ChartColor;
}

/**
 * Barre de répartition à 100 % (« Composition du net », tokens entrée/sortie) :
 * segments séparés par un écart, valeurs négatives ignorées. À accompagner
 * d'une `ChartLegend` ou d'une `KvList` avec les montants.
 */
function ProportionBar({
  segments,
  ariaLabel,
  height = 12,
  className,
}: {
  segments: readonly ProportionSegment[];
  ariaLabel: string;
  height?: number;
  className?: string;
}): React.JSX.Element {
  const positive = segments.filter((segment) => segment.value > 0);
  const total = positive.reduce((sum, segment) => sum + segment.value, 0);
  const gap = positive.length > 1 ? 1 : 0;
  const available = 100 - gap * (positive.length - 1);
  let x = 0;
  return (
    <svg
      viewBox={`0 0 100 ${height}`}
      preserveAspectRatio="none"
      width="100%"
      height={height}
      role="img"
      aria-label={ariaLabel}
      className={cn('block', className)}
    >
      {total <= 0 ? (
        <rect x="0" y="0" width="100" height={height} rx="1" fill="var(--muted)" />
      ) : (
        positive.map((segment) => {
          const width = (segment.value / total) * available;
          const rect = (
            <rect
              key={segment.key}
              x={x}
              y="0"
              width={width}
              height={height}
              rx="1"
              fill={colorVar(segment.color)}
            />
          );
          x += width + gap;
          return rect;
        })
      )}
    </svg>
  );
}

export { ChartLegend, TimeChart, HBars, Meter, MeterCell, ProportionBar };
